import React, { useState, useEffect } from 'react';
import Topbar from '../components/layout/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { usersApi, tenantsApi, formatApiError } from '../lib/api';
import { ROLES } from '../lib/countryConfig';

function genTempPassword() {
  const r = Math.random().toString(36).slice(2, 6).toUpperCase();
  const r2 = Math.floor(Math.random() * 9000 + 1000);
  return `GD-${r}-${r2}`;
}

// Rôles assignables par un admin de tenant (tout sauf superadmin)
const ASSIGNABLE_ROLES = Object.entries(ROLES).filter(([v]) => v !== 'superadmin');

export default function MonTenant() {
  const { profile } = useAuth();
  const { country } = useCountry();
  const [tab, setTab] = useState('team');
  const [users, setUsers] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [lastCreated, setLastCreated] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', role: 'agent', temp_password: genTempPassword() });

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, tRes] = await Promise.all([
        usersApi.list(),
        tenantsApi.myTenant().catch(() => ({ data: null })),
      ]);
      setUsers(uRes.data || []);
      setTenant(tRes.data || null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const agentsCount = users.filter((u) => u.role === 'agent').length;
  const maxAgents = tenant?.max_agents ?? null;
  const quotaReached = maxAgents !== null && agentsCount >= maxAgents;

  const [copied, setCopied] = useState(false);
  const publicLink = tenant?.commune_id ? `${window.location.origin}/signaler/${tenant.commune_id}` : '';
  const copyLink = () => {
    if (!publicLink) return;
    navigator.clipboard?.writeText(publicLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };

  const createUser = async (e) => {
    e?.preventDefault();
    setError(''); setMsg('');
    if (form.role === 'agent' && quotaReached) {
      setError(`Limite d'agents atteinte (${maxAgents}). Contactez votre fournisseur pour augmenter votre forfait.`);
      return;
    }
    setCreating(true);
    try {
      const { data } = await usersApi.create({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        role: form.role,
        temp_password: form.temp_password,
      });
      setLastCreated({ ...data, temp_password: form.temp_password });
      setMsg("✅ Compte créé. Transmettez le mot de passe temporaire — il devra être changé à la 1ʳᵉ connexion.");
      setForm({ email: '', full_name: '', role: 'agent', temp_password: genTempPassword() });
      setTab('team');
      load();
    } catch (ex) {
      setError(formatApiError(ex));
    } finally {
      setCreating(false);
    }
  };

  const toggleActif = async (u) => {
    try { await usersApi.update(u.id, { actif: !u.actif }); load(); } catch (e) { setError(formatApiError(e)); }
  };

  const changeRole = async (u, role) => {
    try { await usersApi.update(u.id, { role }); load(); } catch (e) { setError(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    const newPwd = genTempPassword();
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.full_name || u.email} ?\nNouveau mot de passe : ${newPwd}`)) return;
    try {
      await usersApi.resetPassword(u.id, newPwd);
      setLastCreated({ ...u, temp_password: newPwd, _reset: true });
      setMsg(`🔁 Mot de passe réinitialisé. Transmettez ${newPwd} à ${u.email}.`);
    } catch (e) { setError(formatApiError(e)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.full_name || u.email} ?`)) return;
    try { await usersApi.remove(u.id); setUsers((prev) => prev.filter((x) => x.id !== u.id)); } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="mon-tenant-page">
      <Topbar />
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">🏢 Gestion de mon organisation</h1>
          <p className="text-xs text-gray-500 mt-0.5">{tenant?.nom || profile?.full_name} {tenant?.commune_nom ? `— ${tenant.commune_nom}` : ''}</p>
        </div>
        {maxAgents !== null && (
          <span className={`text-xs px-3 py-1.5 rounded-lg font-semibold ${quotaReached ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-testid="quota-badge">
            👷 {agentsCount} / {maxAgents} agents
          </span>
        )}
      </div>

      <div className="flex gap-2 px-6 pt-4 border-b border-gray-200 bg-white">
        {[
          { v: 'team', l: `👥 Mon équipe (${users.length})` },
          { v: 'create', l: '➕ Ajouter un compte' },
          { v: 'sub', l: '💳 Mon abonnement' },
          { v: 'public', l: '🔗 Lien citoyen' },
        ].map((b) => (
          <button key={b.v} onClick={() => setTab(b.v)} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === b.v ? 'border-blue-600 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-testid={`mt-tab-${b.v}`}>
            {b.l}
          </button>
        ))}
      </div>

      <main className="flex-1 overflow-y-auto p-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 mb-4 text-sm" data-testid="mt-error">⚠️ {error}</div>}
        {msg && <div className="bg-green-50 border border-green-200 text-green-900 rounded-lg px-4 py-2.5 mb-4 text-sm">{msg}</div>}
        {lastCreated && (
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-900 rounded-xl p-4 mb-4 text-sm" data-testid="mt-credentials">
            <div className="font-semibold mb-1">🔑 Identifiants à transmettre</div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><strong>Email :</strong> <span className="font-mono">{lastCreated.email}</span></div>
              <div><strong>Mot de passe temporaire :</strong> <span className="font-mono text-base bg-white px-2 py-0.5 rounded border border-yellow-400">{lastCreated.temp_password}</span></div>
            </div>
            <button onClick={() => setLastCreated(null)} className="text-xs underline mt-2">Masquer</button>
          </div>
        )}

        {tab === 'team' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="mt-users-table">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Rôle</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-500">Chargement…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-500">Aucun utilisateur</td></tr>
                ) : (
                  users.map((u) => {
                    const isSelf = u.id === profile?.id;
                    return (
                      <tr key={u.id} className="border-t border-gray-100" data-testid={`mt-user-${u.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{u.full_name || '—'}{isSelf && <span className="text-[10px] text-gray-400 ml-1">(vous)</span>}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{u.email}</td>
                        <td className="px-4 py-3">
                          <select value={u.role} disabled={isSelf} onChange={(e) => changeRole(u, e.target.value)} className="text-xs px-2 py-1 rounded border border-gray-200 bg-white" data-testid={`mt-role-${u.id}`}>
                            {ASSIGNABLE_ROLES.map(([v, r]) => <option key={v} value={v}>{r.label}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${u.actif ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>{u.actif ? 'Actif' : 'Inactif'}</span>{u.must_change_password && <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-900">⚠️ Temp</span>}</td>
                        <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                          <button onClick={() => resetPassword(u)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`mt-reset-${u.id}`}>🔁 Reset</button>
                          <button onClick={() => toggleActif(u)} disabled={isSelf} className="text-xs px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-40" data-testid={`mt-toggle-${u.id}`}>{u.actif ? '🚫' : '✅'}</button>
                          <button onClick={() => removeUser(u)} disabled={isSelf} className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-40" data-testid={`mt-delete-${u.id}`}>🗑️</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'create' && (
          <form onSubmit={createUser} className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl space-y-4" data-testid="mt-create-form">
            {quotaReached && form.role === 'agent' && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">⚠️ Limite d'agents atteinte ({maxAgents}). Vous ne pouvez pas ajouter d'agent supplémentaire.</div>
            )}
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="mt-email" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nom complet *</label>
              <input required type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="mt-name" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Rôle</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="mt-create-role">
                {ASSIGNABLE_ROLES.map(([v, r]) => <option key={v} value={v}>{r.label}</option>)}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">Le nouveau compte est automatiquement rattaché à votre organisation ({tenant?.commune_nom || 'votre commune'}).</p>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mot de passe temporaire</label>
              <div className="flex gap-2">
                <input type="text" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} className="flex-1 px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-mono outline-none focus:border-blue-600" data-testid="mt-temp-pwd" />
                <button type="button" onClick={() => setForm({ ...form, temp_password: genTempPassword() })} className="px-3 py-2.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">🎲</button>
              </div>
            </div>
            <button type="submit" disabled={creating} className="px-5 py-2.5 rounded-lg text-white font-semibold text-sm disabled:opacity-50" style={{ background: country.palette.primary }} data-testid="mt-create-submit">
              {creating ? '…' : 'Créer le compte'}
            </button>
          </form>
        )}

        {tab === 'sub' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl space-y-4" data-testid="mt-subscription">
            <h2 className="font-semibold text-gray-900">💳 Mon abonnement</h2>
            <p className="text-xs text-gray-500">Pour modifier votre forfait ou augmenter vos quotas, contactez votre fournisseur.</p>
            <div className="grid grid-cols-2 gap-4 text-sm pt-2">
              <Info label="Organisation" value={tenant?.nom} />
              <Info label="Type" value={tenant?.type} />
              <Info label="Forfait" value={(tenant?.plan || '—').toUpperCase()} />
              <Info label="Pays" value={tenant?.country_mode === 'senegal' ? '🌍 Sénégal' : '🍁 Québec'} />
              <Info label={tenant?.country_mode === 'senegal' ? 'Commune' : 'Municipalité'} value={tenant?.commune_nom} />
              <Info label="Statut" value={tenant?.actif === false ? 'Inactif' : 'Actif'} />
              <Info label="Agents" value={maxAgents !== null ? `${agentsCount} / ${maxAgents}` : `${agentsCount} (illimité)`} />
              <Info label="Utilisateurs totaux" value={users.length} />
            </div>
          </div>
        )}
        {tab === 'public' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl space-y-4" data-testid="mt-public-link">
            <h2 className="font-semibold text-gray-900">🔗 Lien de signalement citoyen</h2>
            <p className="text-xs text-gray-500">Partagez ce lien avec vos citoyens (site web, réseaux sociaux, affichage, QR code). Ils pourront signaler un problème <strong>sans créer de compte</strong>. Les signalements arrivent dans « Gestion » avec le badge « 👤 Citoyen » et le statut « À valider ».</p>
            {publicLink ? (
              <>
                <div className="flex gap-2">
                  <input readOnly value={publicLink} className="flex-1 px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm font-mono bg-gray-50" data-testid="mt-public-url" />
                  <button onClick={copyLink} className="px-4 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ background: country.palette.primary }} data-testid="mt-copy-link">{copied ? '✅ Copié' : 'Copier'}</button>
                </div>
                <a href={publicLink} target="_blank" rel="noopener noreferrer" className="inline-block text-xs text-blue-700 underline">Ouvrir la page citoyenne ↗</a>
              </>
            ) : (
              <div className="text-sm text-gray-500">Aucune commune associée à votre organisation. Contactez votre fournisseur.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="border border-gray-100 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">{label}</div>
    <div className="font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);
